import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGoogle, FaLock } from 'react-icons/fa';

import { auth, provider } from '../firebase';
import { glassStyles, colors } from '../theme';
import stavGif from '../assets/loading.gif';

// ============================================
// HERO BACKGROUND IMAGE - Replace this URL!
// ============================================
// Add your Nick/Cumtown image to src/assets/ and import it, or use a URL
const HERO_IMAGE = 'https://lastfm.freetls.fastly.net/i/u/ar0/8c796eed7cb466d75caafda6f9b788e3.jpg'; // Placeholder - podcast studio vibe

// Live animated counter that updates based on time
const LiveStat = ({ baseValue, label, delay, incrementPerSecond }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Calculate current value based on time (seconds since midnight + base)
  const getCurrentValue = () => {
    const now = new Date();
    const secondsToday = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    return baseValue + Math.floor(secondsToday * incrementPerSecond);
  };

  // Initial count-up animation
  useEffect(() => {
    const targetValue = getCurrentValue();
    const timer = setTimeout(() => {
      const duration = 2000;
      const steps = 60;
      const increment = targetValue / steps;
      let current = 0;

      const counter = setInterval(() => {
        current += increment;
        if (current >= targetValue) {
          setCount(targetValue);
          setHasAnimated(true);
          clearInterval(counter);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(counter);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  // Live updates after initial animation
  useEffect(() => {
    if (!hasAnimated) return;

    const interval = setInterval(() => {
      setCount(getCurrentValue());
    }, 1000);

    return () => clearInterval(interval);
  }, [hasAnimated, baseValue, incrementPerSecond]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay / 1000, duration: 0.5 }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem' },
            fontWeight: 700,
            background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {count.toLocaleString()}+
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.muted', textTransform: 'uppercase', letterSpacing: 1 }}>
          {label}
        </Typography>
      </Box>
    </motion.div>
  );
};

// Floating parallax element
const FloatingElement = ({ children, x, y, delay, duration }) => (
  <motion.div
    style={{
      position: 'absolute',
      left: `${x}%`,
      top: `${y}%`,
      pointerEvents: 'none',
    }}
    animate={{
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
    }}
    transition={{
      duration,
      delay,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  >
    {children}
  </motion.div>
);

const SignIn = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Parallax mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePos({
          x: (e.clientX - rect.left - rect.width / 2) / 50,
          y: (e.clientY - rect.top - rect.height / 2) / 50,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Handle redirect result on page load (for mobile)
  useEffect(() => {
    let mounted = true;
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result && result.user) return;
      } catch (err) {
        console.error('Redirect sign in error:', err);
        if (mounted) setError('Sign in failed. Please try again.');
      }
      if (mounted) setLoading(false);
    };
    handleRedirectResult();
    return () => { mounted = false; };
  }, []);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error('Sign in error:', err);
      if (err.code === 'auth/popup-blocked' || err.code === 'auth/popup-closed-by-user') {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectErr) {
          console.error('Redirect fallback error:', redirectErr);
          setError('Sign in failed. Please enable popups or try again.');
        }
      } else {
        setError('Sign in failed. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Full-screen hero background */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3) saturate(1.2)',
            transform: `translate(${mousePos.x}px, ${mousePos.y}px) scale(1.1)`,
            transition: 'transform 0.3s ease-out',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)',
          },
        }}
      />

      {/* Floating parallax elements */}
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }}>
        <FloatingElement x={10} y={20} delay={0} duration={6}>
          <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: `${colors.primary.main}20`, backdropFilter: 'blur(5px)' }} />
        </FloatingElement>
        <FloatingElement x={85} y={15} delay={1} duration={7}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: `${colors.secondary.main}15`, backdropFilter: 'blur(5px)' }} />
        </FloatingElement>
        <FloatingElement x={75} y={70} delay={2} duration={8}>
          <Box sx={{ width: 50, height: 50, borderRadius: '50%', background: `${colors.primary.main}15`, backdropFilter: 'blur(5px)' }} />
        </FloatingElement>
        <FloatingElement x={15} y={75} delay={0.5} duration={5}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `${colors.secondary.main}20`, backdropFilter: 'blur(5px)' }} />
        </FloatingElement>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        {/* Hero section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ width: '100%', maxWidth: 500, textAlign: 'center' }}
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          >
            <Box
              sx={{
                width: { xs: 90, sm: 110 },
                height: { xs: 90, sm: 110 },
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 24px',
                boxShadow: `0 0 60px ${colors.primary.main}50`,
                border: '3px solid rgba(255,255,255,0.1)',
              }}
            >
              <img
                src={stavGif}
                alt="StavBot"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          </motion.div>

          {/* Title */}
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', sm: '3.5rem' },
              background: `linear-gradient(135deg, #fff, ${colors.primary.light})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 60px rgba(255,255,255,0.3)',
            }}
          >
            StavBot
          </Typography>

          <Typography
            sx={{
              mb: 4,
              color: 'rgba(255,255,255,0.7)',
              fontSize: { xs: '1rem', sm: '1.25rem' },
              maxWidth: 400,
              mx: 'auto',
            }}
          >
            AI-powered conversations with Nick Mullen
          </Typography>

          {/* Live stats */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 3, sm: 5 },
              mb: 4,
            }}
          >
            <LiveStat baseValue={127} label="Users" delay={500} incrementPerSecond={0.005} />
            <LiveStat baseValue={2841} label="Messages" delay={700} incrementPerSecond={0.1} />
            <LiveStat baseValue={89} label="Sounds" delay={900} incrementPerSecond={0.003} />
          </Box>

          {/* Login card */}
          <Box
            sx={{
              ...glassStyles.cardElevated,
              p: { xs: 3, sm: 4 },
              mb: 4,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Sign in button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleGoogleSignIn}
              disabled={loading}
              startIcon={!loading && <FaGoogle />}
              sx={{
                py: { xs: 1.5, sm: 2 },
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                background: '#fff',
                color: '#000',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 40px rgba(255,255,255,0.2)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255,255,255,0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? 'Verifying...' : 'Verify You Are A Human With Google'}
            </Button>

            {/* Privacy Notice */}
            <Box
              sx={{
                mt: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <FaLock style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.5)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                No tracking. No spam.
              </Typography>
            </Box>
          </Box>

        </motion.div>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          py: 3,
          color: 'rgba(255,255,255,0.3)',
          fontSize: '0.75rem',
        }}
      >
      </Box>
    </Box>
  );
};

export default SignIn;
